// src/serviceWorkerRegistration.js
//
// Registro del Service Worker para PWA.
//
// CAMBIO IMPORTANTE (15/5/2026): se eliminó el reload automático que disparaba
// `controllerchange`. Antes, cada vez que el SW tomaba control nuevo
// (primera visita por clients.claim() o cada deploy por skipWaiting),
// la página se recargaba sola Y limpiaba la sesión del usuario.
// Ahora: cuando hay update disponible, se dispara un CustomEvent que el
// componente <UpdateNotifier /> escucha y muestra un toast con botón
// "Actualizar". El reload solo ocurre cuando el usuario hace click.

import * as Sentry from '@sentry/react';

// Telemetría — wrapper defensivo. No romper el SW si Sentry no está disponible.
function reportSwUpdate(phase, extra) {
  try {
    Sentry.captureMessage(`sw_update_${phase}`, {
      level: 'info',
      tags: { sw_update: phase, ...(extra || {}) },
    });
  } catch (_) { /* no-op */ }
}

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
  window.location.hostname === '[::1]' ||
  window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

// Flag que se setea en `true` cuando el usuario confirma el update.
// Sin esto, `controllerchange` no dispara reload (evita el bug del 15/5).
let updateApplying = false;

// Guarda anti-duplicación del toast de update.
// `updatefound` y el mensaje `SW_UPDATED` del SW son dos canales que
// pueden dispararse para el mismo evento; queremos mostrar el toast una
// sola vez. Se resetea después del reload (nunca en la misma vida).
let updateNotified = false;

export function register(config) {
  if ('serviceWorker' in navigator) {
    const publicUrl = new URL(process.env.PUBLIC_URL || '', window.location.href);
    if (publicUrl.origin !== window.location.origin) return;

    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL || ''}/service-worker.js`;

      if (isLocalhost) {
        checkValidServiceWorker(swUrl, config);
        navigator.serviceWorker.ready.then(() => {
          console.log('[Condor360] PWA: Service worker activo (localhost).');
        });
      } else {
        registerValidSW(swUrl, config);
      }
    });
  }
}

function registerValidSW(swUrl, config) {
  // Snapshot del estado del controller al load. Un "update real" solo tiene
  // sentido si esta sesión arrancó con una versión anterior corriendo. Si
  // no había controller (primera instalación, incógnito, o Unregister+Ctrl+R),
  // NINGÚN canal debe mostrar el toast, aunque el SW postee SW_UPDATED con
  // firstInstall=false (ese caso ocurre post-Unregister porque el flag de
  // migración vive en Cache Storage, que sobrevive al Unregister).
  const hadControllerAtLoad = !!navigator.serviceWorker.controller;

  // ── Listener de controllerchange ──────────────────────────────────
  // Solo recarga si `updateApplying` está en true (es decir, el usuario
  // ya confirmó el update via toast). En cualquier otro caso lo ignora.
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!updateApplying) return;
    console.log('[Condor360] Nuevo SW activo — recargando la app.');
    window.location.reload();
  });

  navigator.serviceWorker
    .register(swUrl)
    .then(registration => {
      // ── Canal adicional: mensaje SW_UPDATED desde el SW ──────────
      // El SW postea SW_UPDATED cuando activa (deploys normales tras
      // SKIP_WAITING, y también en el force-upgrade one-shot). Es un
      // canal complementario al `updatefound`; la guarda `updateNotified`
      // evita mostrar el toast dos veces si ambos canales disparan.
      // NOTA: aditivo — no pisa los listeners de INSPECTION_SYNCED
      // (CompletionScreen) ni hobbs_final (usePendingHobbs).
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type !== 'SW_UPDATED') return;
        if (updateNotified || updateApplying) return;
        // Si el SW indicó que esta activación fue una primera instalación
        // o migración legacy, NO es un update real → no mostrar toast.
        if (event.data?.firstInstall === true) return;
        // Si esta sesión no tenía controller al cargar, no hay una versión
        // anterior que estemos reemplazando → tampoco es un update real.
        // Este es el caso Unregister+Ctrl+R: el flag de migración persiste
        // en Cache Storage y el SW postea firstInstall=false erróneamente.
        if (!hadControllerAtLoad) return;
        reportSwUpdate('detected', { source: 'sw_updated_message' });
        notifyUpdateAvailable(registration, 'sw_updated_message');
      });

      // Chequear actualizaciones periódicamente (cada 60s)
      setInterval(() => {
        registration.update().catch(() => {});
      }, 60 * 1000);

      // Si al cargar la página ya hay un SW "waiting" (del deploy anterior
      // que el usuario nunca actualizó), notificar para que aparezca el toast.
      if (registration.waiting && navigator.serviceWorker.controller) {
        reportSwUpdate('detected', { source: 'waiting_on_load' });
        notifyUpdateAvailable(registration, 'waiting_on_load');
      }

      registration.onupdatefound = () => {
        const worker = registration.installing;
        if (!worker) return;
        worker.onstatechange = () => {
          if (worker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // Hay un SW activo Y un nuevo SW en waiting → es un UPDATE.
              // Disparar evento para que el toast aparezca.
              reportSwUpdate('detected', { source: 'updatefound' });
              notifyUpdateAvailable(registration, 'updatefound');
              config?.onUpdate?.(registration);
            } else {
              // No hay SW previo controlando → primera instalación.
              // No hay nada que actualizar, app lista para offline.
              console.log('[Condor360] App lista para uso offline.');
              config?.onSuccess?.(registration);
            }
          }
        };
      };
    })
    .catch(err => console.error('[Condor360] Error registrando SW:', err));
}

/**
 * Notifica al resto de la app que hay un update disponible.
 * El componente <UpdateNotifier /> escucha este evento y muestra un toast.
 *
 * El `detail.applyUpdate` es la función que el componente debe llamar cuando
 * el usuario haga click en "Actualizar". Activa el SW nuevo y luego
 * `controllerchange` recarga la página.
 */
function notifyUpdateAvailable(registration, source = 'unknown') {
  // Guarda anti-duplicación: si ya se disparó el toast en esta vida de
  // la app, no volver a emitir el CustomEvent (evita toasts duplicados
  // cuando `updatefound` y `SW_UPDATED` disparan para el mismo update).
  if (updateNotified || updateApplying) return;
  updateNotified = true;
  // Log unificado: TODOS los caminos que muestran el toast pasan por acá.
  // El source permite diagnosticar de qué canal vino sin instrumentación
  // adicional. (Antes el log solo estaba en el camino updatefound, por eso
  // el toast disparado por SW_UPDATED aparecía "silencioso".)
  console.log(`[Condor360] Nueva versión disponible (${source}) — esperando confirmación del usuario.`);

  const applyUpdate = () => {
    // Telemetría: registrar que el usuario tocó "Actualizar" (el comportamiento
    // sigue igual; solo observamos).
    reportSwUpdate('applied', { waiting: registration.waiting ? 'yes' : 'no' });
    if (!registration.waiting) {
      // Edge case: el waiting ya se activó solo (ej. el user cerró y abrió
      // la pestaña). Solo recargar.
      updateApplying = true;
      window.location.reload();
      return;
    }
    updateApplying = true;
    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    // El listener de `controllerchange` (arriba) hará el reload cuando el
    // SW nuevo tome control efectivamente.
  };

  window.dispatchEvent(new CustomEvent('condor360:update-available', {
    detail: { applyUpdate },
  }));
}

function checkValidServiceWorker(swUrl, config) {
  fetch(swUrl, { headers: { 'Service-Worker': 'script' } })
    .then(response => {
      const contentType = response.headers.get('content-type');
      if (response.status === 404 || (contentType && !contentType.includes('javascript'))) {
        navigator.serviceWorker.ready.then(reg => reg.unregister()).then(() => window.location.reload());
      } else {
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => console.log('[Condor360] Sin conexión — app en modo offline.'));
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then(reg => reg.unregister())
      .catch(err => console.error(err));
  }
}
